/**
 * AI视频创作平台 - 用户状态管理
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { User, LoginParams, RegisterParams } from '@shared/types';
import type { ModelPreferences } from '@ai-video/shared';
import * as authApi from '@/api/auth';
import * as modelsApi from '@/api/models';
import { getAuthParams } from '@/lib/csd-adapter';
import { useTeamStore } from '@/stores/team';

/**
 * 判断当前是否存在 CSD Token
 * SDK 未初始化时 getAuthParams 会返回空 token，这里统一兜底为 false
 */
function hasCsdToken(): boolean {
  try {
    return !!getAuthParams().token;
  } catch {
    return false;
  }
}

/**
 * 用户状态管理 Store
 */
export const useUserStore = defineStore('user', () => {
  // ============================================
  // 状态定义
  // ============================================

  /** 当前用户信息 */
  const user = ref<User | null>(null);

  /** 访问令牌 */
  const accessToken = ref<string | null>(localStorage.getItem('accessToken'));

  /** 刷新令牌 */
  const refreshToken = ref<string | null>(localStorage.getItem('refreshToken'));

  /** 登录加载状态 */
  const loading = ref(false);

  /** 用户模型偏好 */
  const modelPreferences = ref<ModelPreferences | null>(null);

  // ============================================
  // 计算属性
  // ============================================

  /** 是否已登录 */
  const isLoggedIn = computed(() => !!accessToken.value || !!user.value);

  /** 用户积分余额 */
  const credits = computed(() => user.value?.credits ?? 0);

  /** 用户昵称（优先显示昵称，其次用户名） */
  const displayName = computed(() => user.value?.nickname || user.value?.username || '');

  // ============================================
  // 方法定义
  // ============================================

  /**
   * 设置用户信息
   * @param userData 用户数据
   */
  function setUser(userData: User) {
    user.value = userData;
  }

  /**
   * 更新用户信息（部分更新）
   * @param userData 用户数据
   */
  function updateUser(userData: Partial<User>) {
    if (user.value) {
      user.value = { ...user.value, ...userData };
    }
  }

  /**
   * 设置令牌
   * @param access 访问令牌
   * @param refresh 刷新令牌
   */
  function setTokens(access: string, refresh: string) {
    accessToken.value = access;
    refreshToken.value = refresh;
    localStorage.setItem('accessToken', access);
    localStorage.setItem('refreshToken', refresh);
  }

  /**
   * 清除用户状态（登出）
   */
  function clearUser() {
    user.value = null;
    accessToken.value = null;
    refreshToken.value = null;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    useTeamStore().clear();
  }

  /**
   * 更新积分余额
   * @param newCredits 新的积分余额
   */
  function updateCredits(newCredits: number) {
    if (user.value) {
      user.value.credits = newCredits;
    }
  }

  /**
   * 扣除积分
   * @param amount 扣除数量
   */
  function deductCredits(amount: number) {
    if (user.value) {
      user.value.credits = Math.max(0, user.value.credits - amount);
    }
  }

  /**
   * 用户登录
   * @param params 登录参数
   */
  async function login(params: LoginParams) {
    loading.value = true;
    try {
      const response = await authApi.login(params);
      setUser(response.user);
      setTokens(response.accessToken, response.refreshToken);
      await useTeamStore().fetchTeamInfo().catch(() => undefined);
      return response;
    } finally {
      loading.value = false;
    }
  }

  /**
   * 用户注册
   * @param params 注册参数
   */
  async function register(params: RegisterParams) {
    loading.value = true;
    try {
      const response = await authApi.register(params);
      setUser(response.user);
      setTokens(response.accessToken, response.refreshToken);
      await useTeamStore().fetchTeamInfo().catch(() => undefined);
      return response;
    } finally {
      loading.value = false;
    }
  }

  /**
   * CSD Token 登录
   * 直接用 SDK 中的 CSD Token 拉取本地用户信息，不再换取本地 JWT
   */
  async function csdLogin() {
    loading.value = true;
    try {
      accessToken.value = null;
      refreshToken.value = null;
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');

      const userInfo = await fetchUserProfile();
      if (!userInfo) {
        throw new Error('CSD Token 不存在或未初始化');
      }
      return userInfo;
    } finally {
      loading.value = false;
    }
  }

  /**
   * 获取用户信息
   */
  async function fetchUserProfile() {
    if (!accessToken.value && !hasCsdToken()) return null;
    
    // 注意：这里不再 catch 后直接 clearUser()
    // 因为请求失败可能是网络问题而非 token 无效
    // 由调用方（initUserState）决定是否清除
    const userInfo = await authApi.getUserProfile();
    setUser(userInfo);
    await useTeamStore().fetchTeamInfo().catch(() => undefined);
    return userInfo;
  }

  /**
   * 刷新Token
   */
  async function refreshAccessToken() {
    if (!refreshToken.value) {
      return false;
    }

    try {
      const response = await authApi.refreshToken(refreshToken.value);
      setTokens(response.accessToken, response.refreshToken);
      return true;
    } catch (error) {
      // refresh 也失败了，才清除登录状态
      clearUser();
      return false;
    }
  }

  /**
   * 登出
   */
  function logout() {
    clearUser();
  }

  /**
   * 初始化用户状态（应用启动时调用）
   */
  async function initUserState() {
    if (accessToken.value || hasCsdToken()) {
      try {
        await fetchUserProfile();
      } catch {
        // 获取用户信息失败，尝试刷新Token
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          try {
            await fetchUserProfile();
          } catch {
            // 刷新后仍然失败，清除登录状态
            clearUser();
          }
        }
        // refreshAccessToken 失败时内部已经 clearUser()
      }
    }
  }

  /**
   * 获取用户模型偏好
   */
  async function fetchModelPreferences() {
    try {
      const response = await modelsApi.getModelPreferences();
      modelPreferences.value = response;
      return response;
    } catch (error) {
      console.error('获取模型偏好失败:', error);
      return null;
    }
  }

  return {
    // 状态
    user,
    accessToken,
    refreshToken,
    loading,
    modelPreferences,
    // 计算属性
    isLoggedIn,
    credits,
    displayName,
    // 方法
    setUser,
    updateUser,
    setTokens,
    clearUser,
    updateCredits,
    deductCredits,
    login,
    register,
    csdLogin,
    fetchUserProfile,
    refreshAccessToken,
    logout,
    initUserState,
    fetchModelPreferences,
  };
});
